import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Icon } from "../lib/Icon";
import { usePortal } from "./state";
import { TICKET_TOPICS, createTicket } from "./tickets";

const fieldLabel: React.CSSProperties = { display: "block", fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "#8a8e88", marginBottom: 6 };
const inputStyle: React.CSSProperties = { width: "100%", padding: "10px 12px", border: "1px solid #dcdad2", borderRadius: 2, fontSize: 14, color: "#1d241f", outline: 0, fontFamily: "inherit", background: "#fff" };

export function PortalNewTicket() {
  const { brand, brandId, spaceId, user } = usePortal();
  const topics = TICKET_TOPICS[brandId] ?? ["Something else"];
  const spaceName = brand.spaces.find((s) => s.id === spaceId)?.name ?? user.org;

  const [topic, setTopic] = useState(topics[0]!);
  const [subject, setSubject] = useState("");
  const [detail, setDetail] = useState("");
  const [submittedId, setSubmittedId] = useState<string | null>(null);

  const canSubmit = subject.trim().length > 0 && detail.trim().length > 0;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    // Mock write: create a ticket in the session store (non-colliding id) so it
    // actually appears under "My tickets" and its thread opens. The real submit
    // lands it in the unified inbox via the authed ticket API (API_MAP.md).
    const t = createTicket({ brand: brandId, space: spaceName, subject: subject.trim(), detail: detail.trim() });
    setSubmittedId(t.id);
  }

  if (submittedId) {
    return (
      <div style={{ maxWidth: 620, margin: "0 auto", padding: "56px 28px", textAlign: "center" }}>
        <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#e8f4ee", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
          <Icon name="check-circle" size={26} color="#1f8a5b" />
        </div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: "-0.5px", color: "#1d241f" }}>Ticket submitted</h1>
        <p style={{ margin: "10px auto 0", fontSize: 14, lineHeight: 1.6, color: "#4a504a", maxWidth: "46ch", textWrap: "pretty" }}>
          We've opened <strong style={{ fontWeight: 600, fontFamily: "'Space Mono', monospace" }}>{submittedId}</strong> for {spaceName}. You'll get an email when a person replies, and you can follow it in My tickets.
        </p>
        <div style={{ marginTop: 22, display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/portal/ticket/$id" params={{ id: submittedId }} style={{ textDecoration: "none", background: brand.ink, color: "#fff", borderRadius: 3, padding: "10px 18px", fontSize: 12, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase" }}>View ticket</Link>
          <Link to="/portal/tickets" style={{ textDecoration: "none", background: "#fff", color: "#4a504a", borderRadius: 3, boxShadow: "inset 0 0 0 1px #dcdad2", padding: "10px 18px", fontSize: 12, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase" }}>My tickets</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "30px 28px 64px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "#8a8e88", marginBottom: 16 }}>
        <Link to="/portal/tickets" style={{ color: "#8a8e88", textDecoration: "none" }}>My tickets</Link>
        <Icon name="chevron-right" size={13} color="#bcbfba" />
        <span>Submit a ticket</span>
      </div>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px", color: "#1d241f" }}>Submit a ticket</h1>
      <p style={{ margin: "6px 0 22px", fontSize: 13.5, color: "#8a8e88", textWrap: "pretty" }}>A person answers every ticket. The more you tell us up front, the faster we can help.</p>

      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ background: "#fafaf7", borderRadius: 3, boxShadow: "inset 0 0 0 1px #ececec", padding: "12px 14px", display: "flex", alignItems: "center", gap: 11 }}>
          <Icon name="building-2" size={16} color={brand.ink} />
          <div style={{ fontSize: 13, color: "#4a504a" }}>Filing as <strong style={{ fontWeight: 600, color: "#1d241f" }}>{user.name}</strong> · {spaceName}</div>
        </div>

        <label style={{ display: "block" }}>
          <span style={fieldLabel}>Topic</span>
          <select value={topic} onChange={(e) => setTopic(e.target.value)} style={{ ...inputStyle, appearance: "auto" }}>
            {topics.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
        </label>

        <label style={{ display: "block" }}>
          <span style={fieldLabel}>Subject</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="A short summary of the problem" style={inputStyle} />
        </label>

        <label style={{ display: "block" }}>
          <span style={fieldLabel}>What's happening?</span>
          <textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={6} placeholder="What you did, what you expected, and what happened instead. Include any IDs or error text." style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
        </label>

        <button type="button" style={{ alignSelf: "flex-start", border: "1px dashed #dcdad2", background: "#fff", borderRadius: 2, padding: "9px 14px", fontSize: 12.5, fontWeight: 600, color: "#4a504a", display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer" }}>
          <Icon name="paperclip" size={14} color="#8a8e88" />
          Attach a screenshot or export
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 12, borderTop: "1px solid #f2f2f2", paddingTop: 16 }}>
          <span style={{ fontSize: 12, color: "#8a8e88" }}>We usually reply within one business day.</span>
          <span style={{ flex: 1 }} />
          <button type="submit" disabled={!canSubmit} style={{ border: 0, background: canSubmit ? brand.ink : "#f0efe9", color: canSubmit ? "#fff" : "#8a8e88", borderRadius: 3, padding: "11px 20px", fontSize: 12, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", cursor: canSubmit ? "pointer" : "not-allowed" }}>Submit ticket</button>
        </div>
      </form>
    </div>
  );
}
